import React, { useCallback, useEffect, useRef, useState } from "react";
import orendaService from "../../services/orenda.service";
import { parseCardCreateResponse } from "./orendaApplicationHelpers";
import { orendaUserManager } from "./orendaUserManager";
import { orendaOidcConfig } from "../../config/orendaOidcConfig";

// Final onboarding screen: issue the applicant's first card. Rendered when
// Orenda reports currentStep === "COMPLETED" (application approved, account
// provisioned).
//
// Backend endpoint: POST api/cardholder/createcardorenda
// Backend DTO: OrendaToken { IdToken, AccessToken, UserId, Email }
// Backend hardcodes cardType: "VIRTUAL" and resolves applicationId/
// accountId/customerId from the DB by UserId.
//
// A sessionStorage flag prevents a duplicate create-card call if the user
// refreshes the page after a successful creation (Orenda would reject a
// second POST with "card already exists" or similar).

const SESSION_FLAG_KEY = "orendaCard";

const readStoredCard = () => {
  try {
    const raw = window.sessionStorage.getItem(SESSION_FLAG_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const storeCard = (card) => {
  try {
    window.sessionStorage.setItem(SESSION_FLAG_KEY, JSON.stringify(card));
  } catch {
    /* ignore quota errors */
  }
};

// Auto-redirect window after a successful card creation. Short enough that
// the "Your card is ready" screen reads as a confirmation moment, long
// enough that the user has time to read the success message and process it.
const REDIRECT_SECONDS = 10;

const OrendaCreateCard = ({ application, onAdvance }) => {
  const cached = readStoredCard();
  const [phase, setPhase] = useState(cached ? "created" : "ready");
  const [card, setCard] = useState(cached);
  const [error, setError] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(REDIRECT_SECONDS);
  const inFlightRef = useRef(false);
  const redirectedRef = useRef(false);

  const createCard = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setPhase("creating");
    setError(null);
    try {
      const response = await orendaService.createCardOrenda();
      const parsed = parseCardCreateResponse(response);
      if (!parsed?.id) {
        throw new Error("Backend returned no card details.");
      }
      storeCard(parsed);
      setCard(parsed);
      setPhase("created");
    } catch (err) {
      if (err?.code === "SSO_SESSION_EXPIRED") {
        setSessionExpired(true);
      } else {
        setError(
          err?.response?.data?.errorMessage || err?.message || String(err)
        );
      }
      setPhase("ready");
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  // If sessionStorage is empty (fresh session, approved earlier), don't auto-
  // fire the create — the user should actively click the button so they know
  // the card is being issued.
  useEffect(() => {
    if (!cached) return undefined;
    // Already created in this session — no action needed.
    return undefined;
  }, [cached]);

  // Shared redirect — called by the countdown and by the "Go to dashboard"
  // button. We ask the parent to refetch viewApplication; once Orenda flips
  // currentStep to "DashBoard" OrendaLanding dispatches to OrendaDashboard
  // and this component unmounts. redirectedRef dedupes repeat clicks.
  const goToDashboard = useCallback(() => {
    if (redirectedRef.current) return;
    redirectedRef.current = true;
    try { onAdvance?.(); } catch { /* parent handles its own errors */ }
  }, [onAdvance]);

  // Count down to auto-redirect once we're on the "created" screen. The
  // timer stops cleanly on unmount; the ref guard means a user who clicks
  // the manual button mid-countdown doesn't trigger a second refetch.
  useEffect(() => {
    if (phase !== "created") return undefined;
    setSecondsLeft(REDIRECT_SECONDS);
    const interval = window.setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          window.clearInterval(interval);
          goToDashboard();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => window.clearInterval(interval);
  }, [phase, goToDashboard]);

  const reAuthenticate = () => {
    orendaUserManager.signinRedirect({
      extraQueryParams: {
        identity_provider: orendaOidcConfig.identity_provider,
        prompt: "login",
      },
    });
  };

  if (sessionExpired) {
    return (
      <>
        <h2 className="orenda-heading">Issue your card</h2>
        <div className="orenda-alert orenda-alert--warn">
          <strong>Your session expired.</strong>
          <div style={{ marginTop: 4, marginBottom: 8 }}>
            Sign in again to issue your card.
          </div>
          <button onClick={reAuthenticate} className="orenda-btn-primary">
            Sign in again
          </button>
        </div>
      </>
    );
  }

  if (phase === "created" && card) {
    return (
      <>
        <h2 className="orenda-heading">Your card is ready</h2>
        <p className="orenda-subheading">
          Your virtual card has been issued and is ready to use.
        </p>

        <CardPreview card={card} applicant={application?.primaryApplicant} />

        <div className="orenda-card">
          <div className="orenda-eyebrow">Card details</div>
          <DetailRow label="Type" value={card.cardType || "VIRTUAL"} />
          <DetailRow label="Status" value={card.status} />
        </div>

        <div className="orenda-actions">
          <span style={{ color: "#6b6f78", fontSize: 13 }}>
            Taking you to your dashboard in <strong>{secondsLeft}s</strong>…
          </span>
          <button
            type="button"
            onClick={goToDashboard}
            className="orenda-btn-primary"
          >
            Go to dashboard
          </button>
        </div>
      </>
    );
  }

  if (phase === "creating") {
    return (
      <>
        <h2 className="orenda-heading">Issuing your card…</h2>
        <p className="orenda-subheading">
          Just a moment — we're setting up your virtual card.
        </p>
        <div className="orenda-card">Processing…</div>
      </>
    );
  }

  // phase === "ready"
  return (
    <>
      <h2 className="orenda-heading">You're approved — issue your card</h2>
      <p className="orenda-subheading">
        Your application is complete. Click below to issue your first virtual
        card.
      </p>

      {error && (
        <div className="orenda-alert orenda-alert--warn">
          <strong>Could not issue card.</strong>
          <div style={{ marginTop: 4 }}>{error}</div>
        </div>
      )}

      <div className="orenda-card">
        <div className="orenda-eyebrow">What happens next</div>
        <ul style={{ margin: "6px 0 0 20px", padding: 0, fontSize: 14, lineHeight: 1.7, color: "#4a4d55" }}>
          <li>We'll issue a virtual card tied to your approved account.</li>
          <li>Your card details appear on this page straight away.</li>
          <li>You can then jump into the Card-Console dashboard to use it.</li>
        </ul>
      </div>

      <div className="orenda-actions">
        <span style={{ color: "#6b6f78", fontSize: 13 }}>
          Only takes a few seconds.
        </span>
        <button onClick={createCard} className="orenda-btn-primary">
          Issue my card
        </button>
      </div>
    </>
  );
};

// Lightweight visual card. Nothing confidential — just the status and ID so
// the user has an immediate "yes this exists" moment before they land in the
// classic dashboard.
const CardPreview = ({ card, applicant }) => {
  const name = [applicant?.firstName, applicant?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    <div
      style={{
        background: "linear-gradient(135deg, #16181d 0%, #2a2d34 100%)",
        color: "#ffffff",
        borderRadius: 10,
        padding: "22px 24px",
        marginBottom: 16,
        boxShadow: "0 4px 18px rgba(0, 0, 0, 0.12)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          top: -40,
          right: -40,
          width: 160,
          height: 160,
          borderRadius: "50%",
          background: "rgba(255, 255, 255, 0.05)",
        }}
      />
      <div style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", opacity: 0.6 }}>
        {card.cardType || "VIRTUAL"} CARD
      </div>
      <div style={{ fontSize: 20, fontWeight: 500, marginTop: 24, letterSpacing: "0.02em" }}>
        •••• •••• •••• ••••
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24, alignItems: "flex-end", gap: 12 }}>
        <div>
          <div style={{ fontSize: 10, letterSpacing: "0.1em", opacity: 0.5, textTransform: "uppercase" }}>
            Cardholder
          </div>
          <div style={{ fontSize: 15, fontWeight: 500, marginTop: 2 }}>
            {name || "Verified cardholder"}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 10, letterSpacing: "0.1em", opacity: 0.5, textTransform: "uppercase" }}>
            Status
          </div>
          <div style={{ fontSize: 13, fontWeight: 500, marginTop: 2, color: "#9ad0a4" }}>
            {card.status || "Active"}
          </div>
        </div>
      </div>
    </div>
  );
};

const DetailRow = ({ label, value }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      padding: "10px 0",
      borderBottom: "1px solid #e6e3db",
      fontSize: 14,
      gap: 12,
    }}
  >
    <span style={{ color: "#6b6f78" }}>{label}</span>
    <span style={{ color: "#16181d", fontFamily: "ui-monospace, monospace", wordBreak: "break-all", textAlign: "right" }}>
      {value}
    </span>
  </div>
);

export default OrendaCreateCard;
